'use client'

// imports
import Link from 'next/link';

// lib files
import { ContentServiceHeroSpotlightLivestreamContent } from '@/lib/types/api/content-service';
import isPBSUrl from '@/lib/helpers/is-pbs-url';

// components
import Badge from '@/components/Badge/Badge';
import ITSImage from '@/components/ITSImage/ITSImage';
import KabobMenu from '@/components/KabobMenu/KabobMenu';
import LinkButton from '@/components/Button/LinkButton';

// svgs
import PlayIcon from "@/public/svg/play.svg";
import ArrowRightIcon from '@/public/svg/arrow-right.svg';

// styles
import styles from './LivestreamHero.module.scss';

interface LivestreamHeroProps {
  livestreamHeroSpotlightCollection: ContentServiceHeroSpotlightLivestreamContent;
}

const LivestreamHero: React.FC<LivestreamHeroProps> = (props) => {
  const {
    livestreamHeroSpotlightCollection,
  } = props;
  const {
    background_image,
    links,
    livestream_show_logo,
    livestream_show_title,
    livestream_short_description,
    livestream_title,
    livestream_url,
   } = livestreamHeroSpotlightCollection;

  // Filter out links with empty url or text
  const validLinks = links?.filter(link => link.url && link.text) || [];

  const livestreamHeroSizes =`
    (min-width: 1700px) 1700px,
    100vw
  `;

  // Check if the livestream_url is a PBS URL and if so, we will open in the same tab.
  // If not, we will open in a new tab for external links.
  const isPBSUrlResult = isPBSUrl(livestream_url);

  const LivestreamHeroLogoOrTitle = () => {
    if (!livestream_show_title) return null;

    return (
      <h2 className={styles.livestream_hero_show_title_logo}>
        <Link
          href={livestream_url}
          target={!isPBSUrlResult ? '_blank' : undefined}
          rel={!isPBSUrlResult ? 'noopener noreferrer' : undefined}
        >
        { livestream_show_logo ? (
          <ITSImage
            src={livestream_show_logo}
            alt={`${livestream_show_title} Logo`}
            width={240}
          />
        ) : (
        <>
          {livestream_show_title}
        </>
        )}
        </Link>
      </h2>
    )
  }

  const LivestreamHeroOverlay = () => {
    // If there is a show title, it's an H2 in the <LivestreamHeroLogoOrTitle>,
    // so the livestream title should be an H3.
    // If there is no show title, make the livestream title an H2.
    const LivestreamTitle = livestream_show_title ? 'h3' : 'h2';
    const hasLinks = validLinks.length > 0;

    return (
      <div className={styles.livestream_hero_overlay}>
        <Badge style="live" className={styles.livestream_hero_live_badge}>
          Live TV
        </Badge>
        <LivestreamHeroLogoOrTitle />
        <LivestreamTitle className={styles.livestream_hero_livestream_title}>
          {livestream_title}
        </LivestreamTitle>
        { livestream_short_description && (
        <p className={styles.livestream_hero_show_description}>
          {livestream_short_description}
        </p>
        )}
        <div className={styles.livestream_hero_buttons}>
          <LinkButton
            href={livestream_url}
            style="white"
            iconBefore='play'
            target={!isPBSUrlResult ? '_blank' : undefined}
          >
            Watch Live
          </LinkButton>

          {hasLinks && (
            <KabobMenu
              className={styles.livestream_hero_kabob_button_above_sm}
              contentClassName={styles.livestream_hero_kabob_content_above_sm}
            >
              <LivestreamHeroKabobMenuItems />
            </KabobMenu>
          )}
        </div>
        {hasLinks && (
          <KabobMenu
            className={styles.livestream_hero_kabob_button_below_sm}
            contentClassName={styles.livestream_hero_kabob_content_below_sm}
          >
            <LivestreamHeroKabobMenuItems />
          </KabobMenu>
        )}
      </div>
    )
  }

  const LivestreamHeroKabobMenuItems = () => (
    <ul>
      <li className={styles.livestream_hero_kabob_item_watch_live}>
        <Link
          href={livestream_url}
          target={!isPBSUrlResult ? '_blank' : undefined}
          rel={!isPBSUrlResult ? 'noopener noreferrer' : undefined}
        >
          <PlayIcon />
          <span>Watch Live</span>
        </Link>
      </li>
      {validLinks.map((link, index) => {
        const isLinkPBSUrl = isPBSUrl(link.url);
        return (
          <li className={styles.livestream_hero_kabob_item_link} key={index}>
            <Link
              href={link.url}
              target={!isLinkPBSUrl ? '_blank' : undefined}
              rel={!isLinkPBSUrl ? 'noopener noreferrer' : undefined}
            >
              <ArrowRightIcon />
              <span>{link.text}</span>
            </Link>
          </li>
        )
      })}
    </ul>
  )

  return (
    <section>
      <div className={styles.livestream_hero}>
        { background_image ?
          <ITSImage
            src={background_image}
            alt={""}
            width={1700}
            height={956}
            resizeWithCrop={true}
            srcSetSizes={[[320,180],[480,270],[768,432],[1024,576],[1700,956]]}
            sizes={livestreamHeroSizes}
            className={styles.livestream_hero_background_image}
            ariaHidden={true}
          /> :
          <div className={styles.livestream_hero_background_fallback} />
        }
        <LivestreamHeroOverlay />
      </div>
    </section>
  );
}

export default LivestreamHero;
